<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { supabase } from '../db/supabaseClient';

const router = useRouter();

// Lightbox para carousel
const lightboxOpen = ref(false);
const lightboxImage = ref(null);

function openLightbox(slide) {
  lightboxImage.value = slide;
  lightboxOpen.value = true;
  document.body.style.overflow = 'hidden';
}

function closeLightbox() {
  lightboxOpen.value = false;
  lightboxImage.value = null;
  document.body.style.overflow = '';
}

// Carousel curvo (estilo fisheye/coverflow)
const carouselIndex = ref(0);
const carouselSlides = [
  { src: '/img/sushi_1.png', alt: 'Sushito - Arte Culinario' },
  { src: '/img/sushi_2.png', alt: 'Sushito - Variedad Premium' },
  { src: '/img/sushi_hero.png', alt: 'Sushito - Platillos Especiales' }
];
let carouselInterval = null;

function nextCarousel() {
  carouselIndex.value = (carouselIndex.value + 1) % carouselSlides.length;
  if (lightboxOpen.value) {
    lightboxImage.value = carouselSlides[carouselIndex.value];
  }
  resetCarouselTimer();
}
function prevCarousel() {
  carouselIndex.value = (carouselIndex.value - 1 + carouselSlides.length) % carouselSlides.length;
  if (lightboxOpen.value) {
    lightboxImage.value = carouselSlides[carouselIndex.value];
  }
  resetCarouselTimer();
}
function goToCarouselSlide(i) {
  carouselIndex.value = i;
  resetCarouselTimer();
}

function resetCarouselTimer() {
  if (carouselInterval) clearInterval(carouselInterval);
  carouselInterval = setInterval(nextCarousel, 4500);
}
function getCarouselSlideStyle(i) {
  const n = carouselSlides.length;
  const idx = carouselIndex.value;
  let offset = i - idx;
  if (offset > n / 2) offset -= n;
  if (offset < -n / 2) offset += n;
  const baseWidth = 420;
  const gap = 32;
  const x = offset * (baseWidth + gap);
  const rotateY = -offset * 28;
  const scale = 1 - Math.abs(offset) * 0.22;
  const z = 100 - Math.abs(offset) * 20;
  const opacity = Math.abs(offset) > 2 ? 0.4 : 0.6 + (1 - Math.abs(offset) * 0.2);
  return {
    '--slide-x': `${x}px`,
    '--slide-rotate-y': `${rotateY}deg`,
    '--slide-scale': scale,
    '--slide-z': z,
    '--slide-opacity': opacity
  };
}

// Typewriter animation
const displayedText = ref('');
const fullText = 'El Arte del Sushi Tradicional';
const isTyping = ref(true);

function typeWriter(text, index = 0) {
  if (index < text.length) {
    displayedText.value = text.substring(0, index + 1);
    setTimeout(() => typeWriter(text, index + 1), 100);
  } else {
    // Esperar un momento antes de empezar a borrar
    setTimeout(() => {
      deleteText(text.length);
    }, 2000);
  }
}

function deleteText(index) {
  if (index > 0) {
    displayedText.value = fullText.substring(0, index - 1);
    setTimeout(() => deleteText(index - 1), 50);
  } else {
    // Esperar un momento antes de empezar a escribir de nuevo
    setTimeout(() => {
      typeWriter(fullText);
    }, 1000);
  }
}

// Scroll Reveal Logic
const revealRefs = ref([]);
const setRevealRef = (el) => {
  if (el) revealRefs.value.push(el);
};

const initRevealObserver = () => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('reveal-visible');
      }
    });
  }, { threshold: 0.1 });

  revealRefs.value.forEach(el => observer.observe(el));
};

// Mouse Parallax Logic
const heroParallax = ref({ x: 0, y: 0 });
function handleHeroMouseMove(e) {
  const { clientX, clientY } = e;
  const { innerWidth, innerHeight } = window;
  const x = (clientX - innerWidth / 2) / innerWidth;
  const y = (clientY - innerHeight / 2) / innerHeight;
  heroParallax.value = { x: x * 20, y: y * 20 };
}

const isScrolled = ref(false);

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50;
};

const scrollProgress = ref(0);
const updateScrollProgress = () => {
  const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
  if (totalHeight > 0) {
    scrollProgress.value = (window.scrollY / totalHeight) * 100;
  }
};

onMounted(() => {
  fetchMenu();
  setTimeout(() => {
    typeWriter(fullText);
  }, 500);
  carouselInterval = setInterval(nextCarousel, 4500);
  initRevealObserver();
  updateScrollProgress();
  window.addEventListener('scroll', handleScroll);
  window.addEventListener('scroll', updateScrollProgress);
});

onUnmounted(() => {
  if (carouselInterval) clearInterval(carouselInterval);
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('scroll', updateScrollProgress);
});

function goToLogin() {
  router.push('/login');
}

function scrollToSection(sectionId) {
  const element = document.getElementById(sectionId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function openWhatsApp() {
  const phoneNumber = '5526692820';
  const message = encodeURIComponent('¡Hola! Me interesa conocer más sobre Sushito.');
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
  window.open(whatsappUrl, '_blank');
}

function makePhoneCall() {
  const phoneNumber = '+5526692820'; // Nuevo número de teléfono
  window.open(`tel:${phoneNumber}`, '_self');
}

function openInNewTab(url, event) {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

const menuItems = ref([]);
const showLoginModal = ref(false);

const fetchMenu = async (showAll = false) => {
  try {
    let query = supabase.from('v_inventory_basic').select('*');

    if (!showAll) {
      query = query.limit(8);
    }

    const { data, error } = await query;

    if (error) throw error;
    if (data && data.length > 0) {
      menuItems.value = data.map(item => ({
        name: item.name,
        description: item.description || 'Delicioso platillo preparado con los mejores ingredientes.',
        price: '$' + item.unit_price,
        image: item.image_base64
          ? `data:${item.image_mime || 'image/jpeg'};base64,${item.image_base64}`
          : '/img/sushi.png'
      }));
    } else {
      console.warn('No products found in v_inventory_basic');
      if (!showAll) menuItems.value = [];
    }
  } catch (error) {
    console.error('Error fetching menu:', error);
    if (!showAll) menuItems.value = [];
  }
};

function handleAddToCart() {
  showLoginModal.value = true;
}

const services = [
  {
    title: 'Pescado de Grado Sushi',
    description: 'Importamos y seleccionamos diariamente el pescado más fresco y de la más alta calidad para garantizar un sabor inigualable.',
    icon: 'set_meal'
  },
  {
    title: 'Maestros Itamae',
    description: 'Nuestros chefs cuentan con décadas de experiencia en el arte tradicional del sushi y la cocina japonesa contemporánea.',
    icon: 'restaurant'
  },
  {
    title: 'Experiencia Omakase',
    description: 'Déjate sorprender por una selección personalizada de los mejores bocados elegidos por nuestro chef.',
    icon: 'celebration'
  },
  {
    title: 'Sake y Maridaje',
    description: 'Contamos con una selecta cava de sakes y vinos diseñados para complementar perfectamente cada una de nuestras piezas.',
    icon: 'wine_bar'
  }
];

const testimonials = [
  {
    name: 'Satoshi Tanaka',
    role: 'Crítico Gastronómico',
    company: 'Tokyo Foodie',
    text: 'La frescura del atún y la técnica del arroz son excepcionales. Sushito ha logrado capturar la esencia de un auténtico sushi bar de Ginza.',
    avatar: 'S',
    rating: 5
  },
  {
    name: 'Elena Rodríguez',
    role: 'Cliente Frecuente',
    company: 'Reseña en Google',
    text: 'El mejor sushi que he probado fuera de Japón. El "Tranquero Special Roll" es una explosión de sabores que no te puedes perder.',
    avatar: 'E',
    rating: 5
  },
  {
    name: 'Marco Polo',
    role: 'Local Guide',
    company: 'TripAdvisor',
    text: 'Un ambiente increíble y un servicio impecable. Los Nigiris se deshacen en la boca. Definitivamente mi lugar favorito para cenas especiales.',
    avatar: 'M',
    rating: 5
  }
];
</script>

<template>
  <div class="landing-page">
    <!-- Hero Section -->
    <section class="hero" @mousemove="handleHeroMouseMove">
      <div class="hero-background">
        <div class="gradient-orb orb-1"
          :style="{ transform: `translate(${heroParallax.x * -0.5}px, ${heroParallax.y * -0.5}px)` }"></div>
        <div class="gradient-orb orb-2"
          :style="{ transform: `translate(${heroParallax.x * 0.3}px, ${heroParallax.y * 0.3}px)` }"></div>
      </div>

      <div class="scroll-progress" :style="{ width: scrollProgress + '%' }"></div>
      <nav class="navbar" :class="{ 'navbar-scrolled': isScrolled }">
        <div class="nav-content">
          <div class="logo">
            <img src="/img/sushi.png" alt="Sushito" class="logo-img" />
            <span class="logo-text">Sushito</span>
          </div>
          <div class="nav-links">
            <a href="#menu" @click.prevent="scrollToSection('menu')" class="nav-link">Menú</a>
            <a href="#services" @click.prevent="scrollToSection('services')" class="nav-link">Servicios</a>
            <a href="#testimonials" @click.prevent="scrollToSection('testimonials')" class="nav-link">Testimonios</a>
            <button @click="goToLogin" class="btn-nav-login">
              <span class="material-symbols-rounded">login</span>
              <span>Iniciar Sesión</span>
            </button>
          </div>
        </div>
      </nav>

      <div class="hero-content">
        <div class="hero-text">
          <div class="hero-badge">
            <span class="material-symbols-rounded">restaurant_menu</span>
            <span>Especialidades de Sushi Premium y Pescado Fresco</span>
          </div>
          <h1 class="hero-title">
            <template v-if="displayedText">
              <span v-if="displayedText.length <= 12">{{ displayedText }}</span>
              <template v-else>
                {{ displayedText.substring(0, 12) }}
                <span class="gradient-text">{{ displayedText.substring(12) }}</span>
              </template>
              <span class="typewriter-cursor">|</span>
            </template>
            <span v-else class="typewriter-cursor">|</span>
          </h1>
          <p class="hero-subtitle">
            Descubre el arte del sushi preparado por maestros Itamae, donde cada bocado es una armonía de frescura y
            tradición
            milenaria.
          </p>
          <div class="hero-buttons" style="display: none;">
            <button @click="scrollToSection('menu')" class="btn-primary-hero">
              <span>VER MENÚ</span>
              <span class="material-symbols-rounded">restaurant</span>
            </button>
            <button @click="makePhoneCall" class="btn-secondary-hero">
              <span class="material-symbols-rounded">calendar_month</span>
              <span>Reservar Mesa</span>
            </button>
          </div>
        </div>

        <div class="hero-visual" :style="{ transform: `translate(${heroParallax.x}px, ${heroParallax.y}px)` }">
          <div class="hero-image-wrapper">
            <img src="/img/sushi_hero.png" alt="Especialidad de Sushi" class="hero-main-img" />
            <div class="hero-image-glow"></div>
            <div class="floating-badge badge-quality">
              <span class="material-symbols-rounded">star</span>
              <div class="badge-text">
                <span class="badge-title">Calidad Premium</span>
                <span class="badge-desc">Ingredientes Frescos</span>
              </div>
            </div>
            <div class="floating-badge badge-taste">
              <span class="material-symbols-rounded">restaurant</span>
              <div class="badge-text">
                <span class="badge-title">+50 Platillos</span>
                <span class="badge-desc">Para Elegir</span>
              </div>
            </div>
          </div>
        </div>
        <div class="hero-visual-old" style="display: none;">
          <!-- Credit Card Flip Effect -->
          <div class="flip-card">
            <div class="flip-card-inner">
              <div class="flip-card-front">
                <p class="heading_8264">MASTERCARD</p>
                <svg class="logo" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="36" height="36"
                  viewBox="0 0 48 48">
                  <path fill="#ff9800" d="M32 10A14 14 0 1 0 32 38A14 14 0 1 0 32 10Z"></path>
                  <path fill="#d50000" d="M16 10A14 14 0 1 0 16 38A14 14 0 1 0 16 10Z"></path>
                  <path fill="#ff3d00"
                    d="M18,24c0,4.755,2.376,8.95,6,11.48c3.624-2.53,6-6.725,6-11.48s-2.376-8.95-6-11.48 C20.376,15.05,18,19.245,18,24z">
                  </path>
                </svg>
                <svg version="1.1" class="chip" id="Layer_1" xmlns="http://www.w3.org/2000/svg"
                  xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="30px" height="30px"
                  viewBox="0 0 50 50" xml:space="preserve">
                  <image id="image0" width="50" height="50" x="0" y="0"
                    href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAMAAAAp4XiDAAAABGdBTUEAALGPC/xhBQAAACBjSFJNAAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAB6VBMVEUAAACNcTiVeUKVeUOYfEaafEeUeUSYfEWZfEaykleyklaXe0SWekSZZjOYfEWYe0WXfUWXe0WcgEicfkiXe0SVekSXekSWekKYe0a9nF67m12ZfUWUeEaXfESVekOdgEmVeUWWekSniU+VeUKVeUOrjFKYfEWliE6WeESZe0GSe0WYfES7ml2Xe0WXeESUeEOWfEWcf0eWfESXe0SXfEWYekSVeUKXfEWxklawkVaZfEWWekOUekOWekSYfESZe0eXekWYfEWZe0WZe0eVeUSWeETAnmDCoWLJpmbxy4P1zoXwyoLIpWbjvXjivnjgu3bfu3beunWvkFWxkle/nmDivXiWekTnwXvkwHrCoWOuj1SXe0TEo2TDo2PlwHratnKZfEbQrWvPrWuafUfbt3PJp2agg0v0zYX0zYSfgkvKp2frxX7mwHrlv3rsxn/yzIPgvHfduXWXe0XuyIDzzISsjVO1lVm0lFitjVPzzIPqxX7duna0lVncuHTLqGjvyIHeuXXxyYGZfUayk1iyk1e2lln1zYTEomO2llrbtnOafkjFpGSbfkfZtXLhvHfkv3nqxH3mwXujhU3KqWizlFilh06khk2fgkqsjlPHpWXJp2erjVOhg0yWe0SliE+XekShhEvAn2D///+gx8TWAAAARnRSTlMACVCTtsRl7Pv7+vxkBab7pZv5+ZlL/UnU/f3SJCVe+Fx39naA9/75XSMh0/3SSkia+pil/KRj7Pr662JPkrbP7OLQ0JFOijI1MwAAAAFiS0dEorDd34wAAAAJcEhZcwAACxMAAAsTAQCanBgAAAAHdElNRQfnAg0IDx2lsiuJAAACLElEQVRIx2NgGAXkAUYmZhZWPICFmYkRVQcbOwenmzse4MbFzc6DpIGXj8PD04sA8PbhF+CFaxEU8iWkAQT8hEVgOkTF/InR4eUVICYO1SIhCRMLDAoKDvFDVhUaEhwUFAjjSUlDdMiEhcOEItzdI6OiYxA6YqODIt3dI2DcuDBZsBY5eVTr4xMSYcyk5BRUOXkFsBZFJTQnp6alQxgZmVloUkrKYC0qqmji2WE5EEZuWB6a lKoKdi35YQUQRkFYPpFaCouKIYzi6EDitJSUlsGY5RWVRGjJLyxNy4ZxqtIqqvOxaVELQwZFZdkIJVU1RSiSalAt6rUwUBdWG1CP6pT6gNqwOrgCdQyHNYR5YQFhDXj8MiK1IAeyN6aORiyBjByVTc0FqBoKWpqwRCVSgilOaY2OaUPw29qjOzqLvTAchpos47u6EZyYnngUSRwpuTe6D+6qaFQdOPNLRzOM1dzhRZyW+CZouHk3dWLXglFcFIflQhj9YWjJGlZcaKAVSvjyPrRQ0oQVKDAQHlYFYUwIm4gqExGmBSkutaVQJeomwViTJqPK6OhCy2Q9sQBk8cY0DxjTJw0lAQWK6cOKfgNhpKK7ZMpUeF3jPa28BCETamiEqJKM+X1gxvWXpoUjVIVPnwErw71nmpgiqiQGBjNzbgs3j1nus+fMndc+Cwm0T52/oNR9lsdCS24ra7Tq1cbWjpXV3sHRCb1idXZ0sGdltXNxRateRwHRAACYHutzk/2I5QAAACV0RVh0ZGF0ZTpjcmVhdGUAMjAyMy0wMi0xM1QwODoxNToyOSswMDowMEUnN7UAAAAldEVYdGRhdGU6bW9kaWZ5ADIwMjMtMDItMTNUMDg6MTU6MjkrMDA6MDA0eo8JAAAAKHRFWHRkYXRlOnRpbWVzdGFtcAAyMDIzLTAyLTEzVDA4OjE1OjI5KzAwOjAwY2+u1gAAAABJRU5ErkJggg==">
                  </image>
                </svg>
                <svg version="1.1" class="contactless" id="Layer_1" xmlns="http://www.w3.org/2000/svg"
                  xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="20px" height="20px"
                  viewBox="0 0 50 50" xml:space="preserve">
                  <image id="image0" width="50" height="50" x="0" y="0"
                    href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAQAAAC0NkA6AAAABGdBTUEAALGPC/xhBQAAACBjSFJNAAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAAAmJLR0QA/4ePzL8AAAAJcEhZcwAACxMAAAsTAQCanBgAAAAHdElNRQfnAg0IEzgIwaKTAAADDklEQVRYw+1XS0iUURQ+f5qPyjQflGRFEEFK76koKGxRbWyVVLSOgsCgwjZBJJYuKogSIoOonUK4q3U0WVBWFPZYiIE6kuArG3VGzK/FfPeMM/MLt99/NuHdfPd888/57jn3nvsQWWj/VcMlvMMd5KRTogqx9iCdIjUUmcGR9ImUYowyP3xNGQJoRLVaZ2DaZf8kyjEJALhI28ELioyiwC+Rc3QZwRYyO/DH51hQgWm6DMIh10KmD4u9O16K49itVoPOAmcGAWWOepXIRScAoJZ2Frro8oN+EyTT6lWkkg6msZfMSR35QTJmjU0g15tIGSJ08ZZMJkJkHpNZgSkyXosS13TkJpZ62mPIJvOSzC1bp8vRhhCakEk7G9/o4gmZdbpsTcKu0m63FbnBP9Qrc15zbkbemfgNDtEOI8NO5L5O9VYyRYgmJayZ9nPaxZrSjW4+F6Uw9yQqIiIZwhp2huQTf6OIvCZyGM6gDJBZbyXifJXr7FZjGXsdxADxI7HUJFB6iWvsIhFpkoiIiGTJfjJfiCuJg2ZEspq9EHGVpYgzKqwJqSAOEwuJQ/pxPvE3cYltJCLdxBLiSKKIE5HxJKcTRNeadxfhDiuYw44zVs1dxKwRk/uCxIiQkxKBsSctRVAge9g1E15EHE6yRUaJecRxcWlukdRIbGFOSZCMWQA/iWauIP3slREHXPyliqBcrrD71AmzZ+rD1Mt2Yr8TZc/UR4/YtFnbijnHi3UrN9vKQ9rPaJf867ZiaqDB+czeKYmd3pNa6fuI75MiC0uXXSR5aEMf7s7a6r/PudVXkjFb/SsrCRfROk0Fx6+H1i9kkTGn/E1vEmt1m089fh+RKdQ5O+xNJPUi cUIjO0Dm7HwvErEr0YxeibL1StSh37STafE4I7zcBdRq1DiOkdmlTJVnkQTBTS7X1FYyvfO4piaInKbDCDaT2anLudYXCRFsQBgAcIF2/Okwgvz5+Z4tsw118dzruvIvjhTB+HOuWy8UvovEH6beitBKxDyxm9MmISKCWrzB7bSlaqGlsf0FC0gMjzTg6GgAAAAldEVYdGRhdGU6Y3JlYXRlADIwMjMtMDItMTNUMDg6MTk6NTYrMDA6MDCjlq7LAAAAJXRFWHRkYXRlOm1vZGlmeQAyMDIzLTAyLTEzVDA4OjE5OjU2KzAwOjAw0ssWdwAAACh0RVh0ZGF0ZTp0aW1lc3RhbXAAMjAyMy0wMi0xM1QwODoxOTo1NiswMDowMIXeN6gAAAAASUVORK5CYII=">
                  </image>
                </svg>
                <p class="number">9759 2484 5269 6576</p>
                <p class="valid_thru">VALID THRU</p>
                <p class="date_8264">1 2 / 2 4</p>
                <p class="name">SUSHITO</p>
              </div>
              <div class="flip-card-back">
                <div class="strip"></div>
                <div class="mstrip"></div>
                <div class="sstrip">
                  <p class="code">***</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Widget Profit and Loss -->
          <div class="floating-widget widget-profit">
            <div class="widget-header">
              <span class="material-symbols-rounded widget-icon">shopping_cart</span>
              <span class="widget-title">Ventas de Hoy</span>
            </div>
            <div class="widget-content">
              <div class="widget-value">$682.5</div>
              <div class="widget-subtitle">Desde 1 Día</div>
              <div class="widget-chart">
                <svg width="100%" height="30" viewBox="0 0 100 30">
                  <polyline points="0,25 20,20 40,15 60,10 80,8 100,5" fill="none" stroke="url(#gradient)"
                    stroke-width="2" />
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" style="stop-color:#C6923D;stop-opacity:1" />
                      <stop offset="100%" style="stop-color:#DFC05E;stop-opacity:1" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>
          </div>

          <!-- Transaction Notifications -->
          <div class="transaction-notifications">
            <div class="notification-card">
              <div class="notification-avatar">M</div>
              <div class="notification-info">
                <div class="notification-amount">$10,000 Dólar</div>
                <div class="notification-from">Recibido de Michael V</div>
              </div>
            </div>
            <div class="notification-card">
              <div class="notification-avatar">J</div>
              <div class="notification-info">
                <div class="notification-amount">$5,200 Dólar</div>
                <div class="notification-from">Recibido de Juan P</div>
              </div>
            </div>
            <div class="notification-card">
              <div class="notification-avatar">L</div>
              <div class="notification-info">
                <div class="notification-amount">$8,500 Dólar</div>
                <div class="notification-from">Recibido de Laura M</div>
              </div>
            </div>
          </div>

          <!-- Small Icon Nodes -->
          <div class="icon-nodes">
            <div class="icon-node node-user">
              <span class="material-symbols-rounded">person</span>
            </div>
            <div class="icon-node node-chart">
              <span class="material-symbols-rounded">trending_up</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Carousel curvo (estilo fisheye) -->
    <section class="carousel-curved-section">
      <div class="carousel-curved-wrap">
        <button type="button" class="carousel-curved-btn carousel-curved-prev" aria-label="Anterior"
          @click="prevCarousel">
          <span class="material-symbols-rounded">chevron_left</span>
        </button>
        <div class="carousel-curved-viewport">
          <div class="carousel-curved-track">
            <div v-for="(slide, i) in carouselSlides" :key="i" class="carousel-curved-slide"
              :class="{ 'is-center': i === carouselIndex }" :style="getCarouselSlideStyle(i)"
              @click="i === carouselIndex ? openLightbox(slide) : goToCarouselSlide(i)">
              <div class="carousel-slide-inner">
                <img :src="slide.src" :alt="slide.alt" loading="lazy" />
                <div v-if="i === carouselIndex" class="zoom-icon-overlay">
                  <span class="material-symbols-rounded">zoom_in</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <button type="button" class="carousel-curved-btn carousel-curved-next" aria-label="Siguiente"
          @click="nextCarousel">
          <span class="material-symbols-rounded">chevron_right</span>
        </button>
      </div>
      <div class="carousel-curved-dots">
        <button v-for="(_, i) in carouselSlides" :key="i" type="button" class="carousel-dot"
          :class="{ active: i === carouselIndex }" :aria-label="`Ir a diapositiva ${i + 1}`"
          @click="goToCarouselSlide(i)" />
      </div>
    </section>

    <!-- Featured Dish Section -->
    <section :ref="setRevealRef" class="featured-dish-section scroll-reveal">
      <div class="section-container">
        <div class="section-header">
          <span class="section-badge">Especialidad</span>
          <h2 class="section-title">Nuestra Firma: <span class="logo-text" style="font-size: inherit; vertical-align: middle;">Sushito</span> Signature Roll</h2>
          <p class="section-subtitle">Una creación audaz que combina técnicas tradicionales con sabores contemporáneos
            únicos.</p>
        </div>
        <div class="featured-dish-content">
          <div class="featured-dish-image-wrapper">
            <img src="/img/sushi_1.png" alt="Sushi Signature Roll" class="featured-dish-image" />
          </div>
          <div class="featured-dish-info">
            <h3 class="featured-dish-name">Sushito Dragon Fire Roll</h3>
            <p class="featured-dish-desc">Tempura de camarón, aguacate, cubierto de atún picante, láminas de serrano y
              un
              toque de salsa anguila artesanal.</p>

          </div>
        </div>
      </div>
    </section>

    <!-- Menu Section -->
    <section id="menu" :ref="setRevealRef" class="menu-section scroll-reveal">
      <div class="section-container">
        <div class="section-header">
          <span class="section-badge">Nuestro Menú</span>
          <h2 class="section-title">Explora Nuestro Menú de Sushi</h2>
        </div>
        <div class="menu-grid">
          <div v-for="item in menuItems" :key="item.name" class="menu-card">
            <div class="menu-image-container">
              <img :src="item.image" :alt="item.name" class="menu-image" />
            </div>
            <h3 class="menu-name">{{ item.name }}</h3>
            <p class="menu-description">{{ item.description }}</p>
            <div class="menu-footer">
              <span class="menu-price">{{ item.price }}</span>
              <button @click="handleAddToCart" class="btn-menu-add"><span
                  class="material-symbols-rounded">add</span></button>
            </div>
          </div>
        </div>
        <div class="menu-show-more">
          <button @click="fetchMenu(true)" class="btn-secondary-hero">Ver Todo El Menú</button>
        </div>
      </div>
    </section>

    <!-- Services Section -->
    <section id="services" :ref="setRevealRef" class="services-section scroll-reveal">
      <div class="section-container">
        <div class="section-header">
          <span class="section-badge">Calidad</span>
          <h2 class="section-title">Tenemos un servicio que te va a satisfacer</h2>
        </div>
        <div class="services-grid">
          <div v-for="service in services" :key="service.title" class="service-card">
            <div class="service-icon-wrapper">
              <span class="material-symbols-rounded feature-icon">{{ service.icon }}</span>
            </div>
            <h3 class="service-title">{{ service.title }}</h3>
            <p class="service-description">{{ service.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Video Section -->
    <section class="video-promo-section">
      <div class="video-promo-content">
        <video src="/video/tranquero.mp4" autoplay muted loop playsinline class="video-promo-bg"></video>
        <div class="video-text-overlay">
          <div class="video-promo-text">
            <h3 class="video-promo-title">Experiencia <span class="logo-text" style="font-size: inherit; vertical-align: middle;">Sushito</span></h3>
            <ul class="video-promo-list">
              <li class="video-promo-item">
                <span class="material-symbols-rounded check-icon">check_circle</span>
                Pescado fresco de grado premium
              </li>
              <li class="video-promo-item">
                <span class="material-symbols-rounded check-icon">check_circle</span>
                Técnica Itamae auténtica
              </li>
              <li class="video-promo-item">
                <span class="material-symbols-rounded check-icon">check_circle</span>
                Arte en cada bocado
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials Section -->
    <section id="testimonials" :ref="setRevealRef" class="testimonials-section scroll-reveal">
      <div class="section-container">
        <div class="section-header">
          <span class="section-badge">Testimonios</span>
          <h2 class="section-title">Lo que dicen nuestros clientes</h2>
        </div>
        <div class="testimonials-grid">
          <div v-for="testimonial in testimonials" :key="testimonial.name" class="testimonial-card">
            <div class="testimonial-rating">
              <span v-for="i in testimonial.rating" :key="i" class="material-symbols-rounded star">star</span>
            </div>
            <p class="testimonial-text">"{{ testimonial.text }}"</p>
            <div class="testimonial-author">
              <div class="testimonial-avatar">{{ testimonial.avatar }}</div>
              <div class="testimonial-info">
                <div class="testimonial-name">{{ testimonial.name }}</div>
                <div class="testimonial-role">{{ testimonial.role }} · {{ testimonial.company }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section :ref="setRevealRef" class="cta-section scroll-reveal">
      <div class="cta-container">
        <div class="cta-content">
          <h2 class="cta-title">¿Antojo de un Sushi Excepcional?</h2>
          <p class="cta-subtitle">
            Reserva tu mesa ahora y vive la verdadera esencia del sushi en Sushito.
          </p>
          <div class="cta-buttons">
            <button @click="makePhoneCall" class="btn-cta-primary">
              <span>Reservar Ahora</span>
              <span class="material-symbols-rounded">arrow_forward</span>
            </button>
            <button @click="scrollToSection('menu')" class="btn-cta-secondary">
              <span class="material-symbols-rounded">restaurant_menu</span>
              <span>Ver Menú</span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="footer-container">
        <div class="footer-brand">
          <div class="logo">
            <img src="/img/sushi.png" alt="Sushito" class="logo-img" />
            <span class="logo-text">Sushito</span>
          </div>
          <p class="footer-text">
            Sistema de punto de venta profesional para hacer crecer tu negocio
          </p>
          <div class="footer-social">
            <a href="https://www.facebook.com/share/1GvLBGS8J7/" target="_blank" rel="noopener noreferrer"
              class="footer-social-link" aria-label="Facebook"
              @click.prevent="openInNewTab('https://www.facebook.com/share/1GvLBGS8J7/')">
              <svg class="footer-social-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path
                  d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </a>
            <a href="https://www.instagram.com/eltranquero.oficial?igsh=MTJubm1sY2ppaWtndQ==" target="_blank"
              rel="noopener noreferrer" class="footer-social-link" aria-label="Instagram"
              @click.prevent="openInNewTab('https://www.instagram.com/eltranquero.oficial?igsh=MTJubm1sY2ppaWtndQ==')">
              <svg class="footer-social-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path
                  d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
            </a>
            <a href="https://www.tiktok.com/@eltranquero1?_r=1&_t=ZS-93Imbu9I0WI" target="_blank"
              rel="noopener noreferrer" class="footer-social-link" aria-label="TikTok"
              @click.prevent="openInNewTab('https://www.tiktok.com/@eltranquero1?_r=1&_t=ZS-93Imbu9I0WI')">
              <svg class="footer-social-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path
                  d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
              </svg>
            </a>
            <a href="https://maps.app.goo.gl/vh22ECFZuFR8CPBg8?g_st=aw" target="_blank" rel="noopener noreferrer"
              class="footer-social-link" aria-label="Ubicación en Google Maps"
              @click.prevent="openInNewTab('https://maps.app.goo.gl/vh22ECFZuFR8CPBg8?g_st=aw')">
              <svg class="footer-social-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path
                  d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
            </a>
          </div>
        </div>
        <div class="footer-links">
          <div class="footer-column">
            <h4 class="footer-title">Restaurante</h4>
            <a href="#menu" @click.prevent="scrollToSection('menu')" class="footer-link">Ver Menú</a>
            <a href="#services" @click.prevent="scrollToSection('services')" class="footer-link">Nuestros Servicios</a>
            <a href="#" class="footer-link">Eventos Especiales</a>
            <a href="#" class="footer-link">Reservaciones</a>
          </div>
          <div class="footer-column">
            <h4 class="footer-title">Empresa</h4>
            <a href="#" class="footer-link">Nuestra Historia</a>
            <a href="#" class="footer-link">Bolsa de Trabajo</a>
            <a href="#" class="footer-link">Prensa</a>
            <a href="#" class="footer-link">Políticas de Privacidad</a>
          </div>
          <div class="footer-column">
            <h4 class="footer-title">Contacto</h4>
            <a href="#" class="footer-link">Ubicación</a>
            <a href="#" class="footer-link">Atención al Cliente</a>
            <a href="#" class="footer-link">Sugerencias</a>
            <a href="#" class="footer-link">Horarios</a>
          </div>
        </div>
      </div>
      <div class="footer-bottom">
        <p>&copy; 2026 Sushito. Creado por Sushito.</p>
      </div>
    </footer>

    <!-- Phone Floating Button -->
    <button @click="makePhoneCall" class="phone-button" aria-label="Llamar por teléfono">
      <span class="material-symbols-rounded phone-icon">phone</span>
      <span class="phone-tooltip">Llámanos ahora</span>
    </button>

    <!-- WhatsApp Floating Button -->
    <button @click="openWhatsApp" class="whatsapp-button" aria-label="Contactar por WhatsApp">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="whatsapp-icon">
        <path
          d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
      </svg>
      <span class="whatsapp-tooltip">¿Necesitas ayuda? Escríbenos</span>
    </button>

    <!-- Lightbox Modal -->
    <Transition name="lightbox-fade">
      <div v-if="lightboxOpen" class="lightbox-overlay" @click.self="closeLightbox">
        <button class="lightbox-close" @click="closeLightbox" aria-label="Cerrar">
          <span class="material-symbols-rounded">close</span>
        </button>
        <button class="lightbox-nav lightbox-prev" @click.stop="prevCarousel" aria-label="Anterior">
          <span class="material-symbols-rounded">chevron_left</span>
        </button>
        <div class="lightbox-content">
          <img v-if="lightboxImage" :src="lightboxImage.src" :alt="lightboxImage.alt" class="lightbox-image" />
        </div>
        <button class="lightbox-nav lightbox-next" @click.stop="nextCarousel" aria-label="Siguiente">
          <span class="material-symbols-rounded">chevron_right</span>
        </button>
        <div class="lightbox-counter">
          {{ carouselIndex + 1 }} / {{ carouselSlides.length }}
        </div>
      </div>
    </Transition>

    <!-- Login Required Modal -->
    <Transition name="modal">
      <div v-if="showLoginModal" class="modal-overlay" @click.self="showLoginModal = false">
        <div class="modal-container">
          <div class="modal-content">
            <div class="icon-wrapper warning">
              <span class="material-symbols-rounded icon">warning</span>
            </div>
            <h2 class="modal-title warning">Inicio de Sesión Requerido</h2>
            <p class="modal-message">Debes iniciar sesión para agregar productos al carrito y realizar pedidos.</p>

            <div class="modal-actions">
              <button class="btn-modal-cancel" @click="showLoginModal = false">Cancelar</button>
              <button class="btn-modal-confirm warning" @click="goToLogin">Iniciar Sesión</button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Cinzel:wght@600;700;800&family=Dancing+Script:wght@700&display=swap');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.landing-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
  color: #ffffff;
  overflow-x: hidden;
  position: relative;
  scroll-behavior: smooth;
  width: 100%;
  max-width: 100vw;
}

/* Hero Background */
.hero-background {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.4;
  animation: float 25s infinite ease-in-out;
}

.orb-1 {
  width: 700px;
  height: 700px;
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
  top: -300px;
  right: -200px;
  animation-delay: 0s;
}

.orb-2 {
  width: 600px;
  height: 600px;
  background: linear-gradient(135deg, #DFC05E 0%, #F7ED7D 100%);
  bottom: -200px;
  left: -150px;
  animation-delay: 12s;
}

@keyframes float {

  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }

  33% {
    transform: translate(40px, -40px) scale(1.1);
  }

  66% {
    transform: translate(-30px, 30px) scale(0.9);
  }
}

/* Navbar */
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 100;
  padding: 1.5rem 2rem;
  background: rgba(10, 11, 20, 0.85);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(198, 146, 61, 0.3);
}

.scroll-progress {
  position: fixed;
  top: 0;
  left: 0;
  height: 4px;
  background: linear-gradient(90deg, #FFD700, #C6923D, #DFC05E);
  z-index: 9999;
  transition: width 0.15s ease-out;
  box-shadow: 0 0 15px rgba(255, 215, 0, 0.6);
  pointer-events: none;
}

.nav-content {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.nav-content .logo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: transform 0.3s ease;
  flex-shrink: 0;
  order: 1;
  position: relative;
  top: auto;
  left: auto;
  width: auto;
  height: auto;
}

.nav-content .logo:hover {
  transform: scale(1.05);
}

.nav-content .logo-img {
  width: 50px;
  height: 50px;
  object-fit: contain;
  filter: drop-shadow(0 0 15px rgba(198, 146, 61, 0.8));
  transition: filter 0.3s ease;
  display: block;
  flex-shrink: 0;
}

.nav-content .logo:hover .logo-img {
  filter: drop-shadow(0 0 25px rgba(198, 146, 61, 1));
}

.logo-text {
  font-size: 2.5rem;
  font-weight: 700;
  font-family: 'Dancing Script', cursive;
  background: linear-gradient(135deg, #FFD700 0%, #C6923D 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 0.05em;
  white-space: nowrap;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

@keyframes shimmer {

  0%,
  100% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 2rem;
  order: 2;
  margin-left: auto;
}

.nav-link {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
  scroll-behavior: smooth;
}

.nav-link:hover {
  color: #DFC05E;
}

.btn-nav-login {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.25) 0%, rgba(223, 192, 94, 0.2) 100%);
  border: 1px solid rgba(198, 146, 61, 0.5);
  border-radius: 12px;
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.btn-nav-login:hover {
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.35) 0%, rgba(223, 192, 94, 0.3) 100%);
  transform: translateY(-2px);
  border-color: rgba(198, 146, 61, 0.7);
}

/* Hero Content */
.hero-content {
  position: relative;
  z-index: 10;
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 2rem;
  padding-top: calc(4rem + 100px);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  min-height: calc(100vh - 100px);
}

.hero-text {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: rgba(247, 237, 125, 0.15);
  border: 1px solid rgba(198, 146, 61, 0.5);
  border-radius: 50px;
  color: #DFC05E;
  font-size: 0.875rem;
  font-weight: 600;
  width: fit-content;
  backdrop-filter: blur(10px);
}

.hero-badge .material-symbols-rounded {
  font-size: 1.125rem;
  color: #C6923D;
}

.hero-title {
  font-size: 4rem;
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: -0.02em;
  min-height: 1.1em;
}

.typewriter-cursor {
  display: inline-block;
  animation: blink 1s infinite;
  color: #C6923D;
  font-weight: 300;
}

@keyframes blink {

  0%,
  50% {
    opacity: 1;
  }

  51%,
  100% {
    opacity: 0;
  }
}

.gradient-text {
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.25rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.8);
  max-width: 550px;
}

.hero-buttons {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.btn-primary-hero {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
  border: 2px solid rgba(198, 146, 61, 0.6);
  border-radius: 14px;
  color: #000000;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(198, 146, 61, 0.4);
}

.btn-primary-hero:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 40px rgba(198, 146, 61, 0.6);
  background: linear-gradient(135deg, #DFC05E 0%, #F7ED7D 100%);
}

.btn-secondary-hero {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: rgba(247, 237, 125, 0.1);
  border: 2px solid rgba(198, 146, 61, 0.4);
  border-radius: 14px;
  color: #DFC05E;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.btn-secondary-hero:hover {
  background: rgba(198, 146, 61, 0.2);
  border-color: rgba(198, 146, 61, 0.7);
  color: #F7ED7D;
}

/* Hero Visual */
.hero-visual {
  position: relative;
  height: 600px;
}

/* Credit Card Flip Effect */
.flip-card {
  background-color: transparent;
  width: 400px;
  height: 256px;
  perspective: 1000px;
  color: white;
  position: absolute;
  top: 55%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 100;
  animation: floatCard 5s infinite ease-in-out;
}

@keyframes floatCard {

  0%,
  100% {
    transform: translate(-50%, -50%) translateY(0);
  }

  50% {
    transform: translate(-50%, -50%) translateY(-10px);
  }
}

.heading_8264 {
  position: absolute;
  letter-spacing: .25em;
  font-size: 0.65em;
  top: 2.5em;
  left: 20em;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
}

.flip-card .logo {
  position: absolute;
  top: 8.5em;
  left: 15em;
  width: 48px;
  height: 48px;
}

.chip {
  position: absolute;
  top: 2.8em;
  left: 2em;
  width: 40px;
  height: 40px;
}

.contactless {
  position: absolute;
  top: 4.5em;
  left: 16em;
  width: 28px;
  height: 28px;
}

.number {
  position: absolute;
  font-weight: bold;
  font-size: 0.85em;
  top: 10.5em;
  left: 2em;
  letter-spacing: 0.15em;
  color: rgba(255, 255, 255, 0.95);
}

.valid_thru {
  position: absolute;
  font-weight: bold;
  top: 17em;
  font-size: 0.5em;
  left: 4em;
  color: rgba(255, 255, 255, 0.7);
  letter-spacing: 0.15em;
}

.date_8264 {
  position: absolute;
  font-weight: bold;
  font-size: 0.65em;
  top: 18.5em;
  left: 4em;
  color: rgba(255, 255, 255, 0.95);
  letter-spacing: 0.1em;
}

.name {
  position: absolute;
  font-weight: bold;
  font-size: 0.65em;
  top: 20.5em;
  left: 2.5em;
  color: rgba(255, 255, 255, 0.95);
  letter-spacing: 0.15em;
}

.strip {
  position: absolute;
  background-color: black;
  width: calc(100% - 3rem);
  height: 2.5em;
  top: 3.5em;
  left: 1.5rem;
  right: 1.5rem;
  margin: 0 auto;
  background: repeating-linear-gradient(45deg,
      #303030,
      #303030 10px,
      #202020 10px,
      #202020 20px);
  border-radius: 2px;
}

.mstrip {
  position: absolute;
  background-color: rgb(255, 255, 255);
  width: 12em;
  height: 1.2em;
  top: 7.5em;
  left: 1.5rem;
  border-radius: 4px;
}

.sstrip {
  position: absolute;
  background-color: rgb(255, 255, 255);
  width: 6em;
  height: 1.2em;
  top: 7.5em;
  left: 14.5rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.code {
  font-weight: bold;
  text-align: center;
  color: black;
  font-size: 0.9em;
  margin: 0;
  padding: 0;
  line-height: 1.2em;
}

.flip-card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  text-align: center;
  transition: transform 0.8s;
  transform-style: preserve-3d;
}

.flip-card:hover .flip-card-inner {
  transform: rotateY(180deg);
}

.flip-card-front,
.flip-card-back {
  box-shadow: 0 8px 14px 0 rgba(0, 0, 0, 0.2);
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  border-radius: 1rem;
}

.flip-card-front {
  box-shadow: rgba(0, 0, 0, 0.4) 0px 2px 2px, rgba(0, 0, 0, 0.3) 0px 7px 13px -3px, rgba(0, 0, 0, 0.2) 0px -1px 0px inset;
  background: linear-gradient(135deg, #724410 0%, #9D6B27 100%);
  border: 1px solid rgba(198, 146, 61, 0.4);
  padding: 1.5rem;
}

.flip-card-back {
  box-shadow: rgba(0, 0, 0, 0.4) 0px 2px 2px, rgba(0, 0, 0, 0.3) 0px 7px 13px -3px, rgba(0, 0, 0, 0.2) 0px -1px 0px inset;
  background: linear-gradient(135deg, #724410 0%, #9D6B27 100%);
  border: 1px solid rgba(198, 146, 61, 0.4);
  transform: rotateY(180deg);
  padding: 0;
  position: relative;
  overflow: hidden;
}

/* Floating Widgets */
.floating-widget {
  position: absolute;
  background: rgba(198, 146, 61, 0.2);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(198, 146, 61, 0.4);
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 10px 40px rgba(198, 146, 61, 0.3);
  z-index: 6;
  animation: floatWidget 6s infinite ease-in-out;
}

.widget-profit {
  top: 5%;
  left: 0;
  width: 220px;
  animation-delay: 0s;
}

@keyframes floatWidget {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-15px);
  }
}

.widget-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.widget-icon {
  font-size: 1.25rem;
  color: #C6923D;
}

.widget-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.widget-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.widget-value {
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
}

.widget-subtitle {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
}

.widget-chart {
  margin-top: 0.5rem;
  height: 40px;
}

/* Transaction Notifications */
.transaction-notifications {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  z-index: 6;
}

.notification-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(198, 146, 61, 0.2);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(198, 146, 61, 0.4);
  border-radius: 12px;
  width: 260px;
  box-shadow: 0 8px 30px rgba(198, 146, 61, 0.3);
  transition: all 0.3s ease;
}

.notification-card:hover {
  transform: translateX(-5px);
  box-shadow: 0 12px 40px rgba(198, 146, 61, 0.5);
}

.notification-avatar {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #000000;
  flex-shrink: 0;
}

.notification-info {
  flex: 1;
}

.notification-amount {
  font-size: 0.9rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.25rem;
}

.notification-from {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
}

/* Icon Nodes */
.icon-nodes {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
}

.icon-node {
  position: absolute;
  width: 55px;
  height: 55px;
  border-radius: 50%;
  background: rgba(198, 146, 61, 0.25);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(198, 146, 61, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #C6923D;
  box-shadow: 0 4px 20px rgba(198, 146, 61, 0.3);
  animation: pulseNode 3s infinite ease-in-out;
}

.icon-node .material-symbols-rounded {
  font-size: 1.75rem;
}

.node-user {
  bottom: 15%;
  left: 10%;
  animation-delay: 0s;
}

.node-chart {
  bottom: 20%;
  right: 15%;
  animation-delay: 1.5s;
}

@keyframes pulseNode {

  0%,
  100% {
    transform: scale(1);
    opacity: 0.7;
  }

  50% {
    transform: scale(1.1);
    opacity: 1;
  }
}

/* Carousel curvo (fisheye / coverflow) */
.carousel-curved-section {
  padding: 4rem 0;
  background: linear-gradient(180deg, rgba(10, 11, 20, 0.4) 0%, rgba(10, 11, 20, 0.6) 100%);
  overflow: hidden;
}

.carousel-curved-wrap {
  position: relative;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 4rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  z-index: 10;
}

.carousel-curved-btn {
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 2px solid rgba(198, 146, 61, 0.7);
  background: rgba(198, 146, 61, 0.25);
  backdrop-filter: blur(10px);
  color: #DFC05E;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;
  z-index: 200;
  position: relative;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.carousel-curved-btn:hover {
  background: rgba(198, 146, 61, 0.4);
  border-color: rgba(198, 146, 61, 1);
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(198, 146, 61, 0.5);
}

.carousel-curved-btn .material-symbols-rounded {
  font-size: 1.85rem;
  font-weight: 600;
}

.carousel-curved-viewport {
  flex: 1;
  min-height: 420px;
  perspective: 1400px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

.carousel-curved-track {
  position: relative;
  width: 100%;
  height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform-style: preserve-3d;
  perspective: 1400px;
}

.carousel-curved-slide {
  position: absolute;
  width: 420px;
  height: 300px;
  border-radius: 20px;
  overflow: visible;
  box-shadow: 0 16px 50px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(198, 146, 61, 0.2);
  cursor: pointer;
  transition: transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    opacity 0.5s ease,
    box-shadow 0.35s ease;
  left: 50%;
  top: 50%;
  margin-left: -210px;
  margin-top: -150px;
  transform: translateX(var(--slide-x, 0)) rotateY(var(--slide-rotate-y, 0deg)) scale(var(--slide-scale, 1));
  opacity: var(--slide-opacity, 1);
  z-index: calc(50 + var(--slide-z, 100));
  backface-visibility: hidden;
  background: rgba(0, 0, 0, 0.05);
}

.carousel-slide-inner {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 20px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.1);
}


.carousel-curved-slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.carousel-curved-slide.is-center {
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.5), 0 0 0 2px rgba(198, 146, 61, 0.5);
  z-index: 150;
  cursor: pointer;
}


.carousel-curved-dots {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
  padding: 0 1rem;
}

.carousel-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: rgba(198, 146, 61, 0.35);
  cursor: pointer;
  transition: all 0.25s ease;
}

.carousel-dot:hover {
  background: rgba(198, 146, 61, 0.6);
  transform: scale(1.2);
}

.carousel-dot.active {
  background: #DFC05E;
  width: 28px;
  border-radius: 5px;
}

@media (max-width: 900px) {
  .carousel-curved-wrap {
    padding: 0 3rem;
  }

  .carousel-curved-btn {
    width: 44px;
    height: 44px;
    z-index: 200;
  }

  .carousel-curved-btn .material-symbols-rounded {
    font-size: 1.6rem;
  }

  .carousel-curved-viewport {
    min-height: 340px;
  }

  .carousel-curved-track {
    height: 320px;
  }

  .carousel-curved-slide {
    width: 320px;
    height: 230px;
    margin-left: -160px;
    margin-top: -115px;
  }
}

@media (max-width: 600px) {
  .carousel-curved-section {
    padding: 3rem 0;
  }

  .carousel-curved-wrap {
    padding: 0 2rem;
  }

  .carousel-curved-btn {
    width: 40px;
    height: 40px;
    z-index: 200;
  }

  .carousel-curved-btn .material-symbols-rounded {
    font-size: 1.5rem;
  }

  .carousel-curved-slide {
    width: 260px;
    height: 185px;
    margin-left: -130px;
    margin-top: -93px;
  }

  .carousel-curved-viewport {
    min-height: 280px;
  }

  .carousel-curved-track {
    height: 260px;
  }
}

/* Stats Section */
.stats-section {
  padding: 4rem 2rem;
  background: rgba(247, 237, 125, 0.05);
  border-top: 1px solid rgba(198, 146, 61, 0.3);
  border-bottom: 1px solid rgba(198, 146, 61, 0.3);
}

.stats-container {
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
}

.stat-card {
  text-align: center;
  padding: 2rem;
  background: rgba(247, 237, 125, 0.08);
  border: 1px solid rgba(198, 146, 61, 0.3);
  border-radius: 20px;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.stat-card:hover {
  background: rgba(198, 146, 61, 0.15);
  border-color: rgba(198, 146, 61, 0.6);
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(198, 146, 61, 0.4);
}

.stat-icon {
  margin-bottom: 1rem;
}

.stat-icon .material-symbols-rounded {
  font-size: 2.5rem;
  color: #C6923D;
}

.stat-value {
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.5rem;
}

.stat-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
}

/* Section Styles */
.section-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 6rem 2rem;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-badge {
  display: inline-block;
  padding: 0.5rem 1.25rem;
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.3) 0%, rgba(223, 192, 94, 0.25) 100%);
  border: 1px solid rgba(198, 146, 61, 0.5);
  border-radius: 50px;
  color: #DFC05E;
  font-size: 0.875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, #ffffff 0%, #DFC05E 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.section-subtitle {
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.75);
  max-width: 600px;
  margin: 0 auto;
}

/* Features Section */
.features-section {
  background: rgba(10, 11, 20, 0.5);
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.feature-card {
  padding: 2.5rem;
  background: linear-gradient(135deg, rgba(247, 237, 125, 0.1) 0%, rgba(247, 237, 125, 0.05) 100%);
  border: 1px solid rgba(198, 146, 61, 0.3);
  border-radius: 24px;
  transition: all 0.4s ease;
  backdrop-filter: blur(20px);
}

.feature-card:hover {
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.2) 0%, rgba(223, 192, 94, 0.15) 100%);
  border-color: rgba(198, 146, 61, 0.7);
  transform: translateY(-10px);
  box-shadow: 0 25px 50px rgba(198, 146, 61, 0.4);
}

.feature-icon-wrapper {
  margin-bottom: 1.5rem;
}

.feature-icon {
  font-size: 3rem;
  color: #C6923D;
}

.feature-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #ffffff;
}

.feature-description {
  font-size: 1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
}

/* Benefits Section */
.benefits-section {
  background: rgba(247, 237, 125, 0.05);
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.benefit-card {
  padding: 2.5rem;
  background: rgba(247, 237, 125, 0.08);
  border: 1px solid rgba(198, 146, 61, 0.3);
  border-radius: 24px;
  transition: all 0.4s ease;
  backdrop-filter: blur(20px);
}

.benefit-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 25px 50px rgba(198, 146, 61, 0.4);
  border-color: rgba(198, 146, 61, 0.6);
}

.benefit-icon {
  width: 70px;
  height: 70px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.benefit-icon .material-symbols-rounded {
  font-size: 2.5rem;
}

.benefit-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #ffffff;
}

.benefit-description {
  font-size: 1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
}

/* Pricing Section */
.pricing-section {
  background: rgba(10, 11, 20, 0.5);
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;
}

.pricing-card {
  padding: 2.5rem;
  background: linear-gradient(135deg, rgba(247, 237, 125, 0.1) 0%, rgba(247, 237, 125, 0.05) 100%);
  border: 1px solid rgba(198, 146, 61, 0.3);
  border-radius: 24px;
  transition: all 0.4s ease;
  backdrop-filter: blur(20px);
  position: relative;
}

.pricing-card:hover {
  transform: translateY(-10px);
  border-color: rgba(198, 146, 61, 0.7);
  box-shadow: 0 25px 50px rgba(198, 146, 61, 0.4);
}

.pricing-card.featured {
  border-color: rgba(198, 146, 61, 0.7);
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.2) 0%, rgba(223, 192, 94, 0.15) 100%);
  transform: scale(1.05);
}

.pricing-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
  color: #000000;
  padding: 0.5rem 1.5rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.pricing-header {
  margin-bottom: 2rem;
}

.pricing-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 1rem;
}

.pricing-price {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.price-amount {
  font-size: 3rem;
  font-weight: 800;
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.price-period {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.6);
}

.pricing-features {
  list-style: none;
  padding: 0;
  margin: 0 0 2rem 0;
}

.pricing-features li {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
}

.pricing-features li .material-symbols-rounded {
  font-size: 1.25rem;
  color: #C6923D;
}

.btn-pricing {
  width: 100%;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
  color: #000000;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-pricing:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(198, 146, 61, 0.5);
  background: linear-gradient(135deg, #DFC05E 0%, #F7ED7D 100%);
}

/* Integrations Section */
.integrations-section {
  background: rgba(10, 11, 20, 0.5);
}

.integrations-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 1.5rem;
}

.integration-card {
  padding: 2rem;
  background: rgba(247, 237, 125, 0.08);
  border: 1px solid rgba(198, 146, 61, 0.3);
  border-radius: 16px;
  text-align: center;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.integration-card:hover {
  transform: translateY(-5px);
  border-color: rgba(198, 146, 61, 0.6);
  box-shadow: 0 10px 30px rgba(198, 146, 61, 0.4);
}

.integration-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
}

.integration-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

/* Testimonials Section */
.testimonials-section {
  background: rgba(247, 237, 125, 0.05);
}

.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.testimonial-card {
  padding: 2.5rem;
  background: rgba(247, 237, 125, 0.08);
  border: 1px solid rgba(198, 146, 61, 0.3);
  border-radius: 24px;
  backdrop-filter: blur(20px);
  transition: all 0.3s ease;
}

.testimonial-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(198, 146, 61, 0.4);
  border-color: rgba(198, 146, 61, 0.6);
}

.testimonial-rating {
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1.5rem;
}

.star {
  color: #DFC05E;
  font-size: 1.25rem;
}

.testimonial-text {
  font-size: 1.125rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
  font-style: italic;
}

.testimonial-author {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.testimonial-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #000000;
  font-size: 1.25rem;
}

.testimonial-name {
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.25rem;
}

.testimonial-role {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
}

/* CTA Section */
.cta-section {
  padding: 6rem 2rem;
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.25) 0%, rgba(223, 192, 94, 0.2) 100%);
  border-top: 1px solid rgba(198, 146, 61, 0.4);
  border-bottom: 1px solid rgba(198, 146, 61, 0.4);
}

.cta-container {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}

.cta-title {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  letter-spacing: -0.02em;
}

.cta-subtitle {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 2.5rem;
}

.cta-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-cta-primary {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 2.5rem;
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
  border: none;
  border-radius: 14px;
  color: #000000;
  font-size: 1.125rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 15px 40px rgba(198, 146, 61, 0.5);
}

.btn-cta-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 20px 50px rgba(198, 146, 61, 0.6);
  background: linear-gradient(135deg, #DFC05E 0%, #F7ED7D 100%);
}

.btn-cta-secondary {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 2.5rem;
  background: rgba(247, 237, 125, 0.15);
  border: 2px solid rgba(198, 146, 61, 0.5);
  border-radius: 14px;
  color: #DFC05E;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.btn-cta-secondary:hover {
  background: rgba(198, 146, 61, 0.25);
  border-color: rgba(198, 146, 61, 0.7);
  color: #F7ED7D;
}

/* Footer */
.footer {
  padding: 4rem 2rem 2rem;
  background: rgba(10, 11, 20, 0.9);
  border-top: 1px solid rgba(198, 146, 61, 0.3);
}

.footer-container {
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 4rem;
  margin-bottom: 3rem;
}

.footer-brand {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.footer .logo-img {
  width: 100px;
  height: auto;
  filter: drop-shadow(0 0 10px rgba(198, 146, 61, 0.4));
}

.footer .logo {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
}

.footer .logo-text {
  font-size: 1.8rem;
}

.footer-text {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  line-height: 1.6;
}

.footer-social {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.footer-social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.08);
  border-radius: 50%;
  transition: color 0.3s ease, background 0.3s ease, transform 0.2s ease;
  cursor: pointer;
  text-decoration: none;
  position: relative;
  z-index: 1;
}

.footer-social-link:hover {
  color: #DFC05E;
  background: rgba(223, 192, 94, 0.2);
  transform: translateY(-2px);
}

.footer-social-icon {
  width: 20px;
  height: 20px;
  pointer-events: none;
}

.footer-links {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.footer-title {
  font-size: 1rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 1rem;
}

.footer-link {
  display: block;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 0.9rem;
  margin-bottom: 0.75rem;
  transition: color 0.3s ease;
}

.footer-link:hover {
  color: #DFC05E;
}

.footer-bottom {
  max-width: 1400px;
  margin: 0 auto;
  padding-top: 2rem;
  border-top: 1px solid rgba(198, 146, 61, 0.3);
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.875rem;
}

/* WhatsApp Floating Button */
.whatsapp-button {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 8px 25px rgba(37, 211, 102, 0.4);
  z-index: 1000;
  transition: all 0.3s ease;
  overflow: visible;
}

.whatsapp-button:hover {
  transform: scale(1.1);
  box-shadow: 0 12px 35px rgba(37, 211, 102, 0.6);
}

.whatsapp-button:active {
  transform: scale(0.95);
}

.whatsapp-icon {
  width: 32px;
  height: 32px;
  fill: #ffffff;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.whatsapp-tooltip {
  position: absolute;
  right: 75px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(10, 11, 20, 0.95);
  color: #ffffff;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(198, 146, 61, 0.4);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.whatsapp-tooltip::after {
  content: '';
  position: absolute;
  right: -8px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-left: 8px solid rgba(10, 11, 20, 0.95);
  border-left-color: rgba(10, 11, 20, 0.95);
}

.whatsapp-button:hover .whatsapp-tooltip {
  opacity: 1;
  right: 80px;
}

/* Phone Floating Button */
.phone-button {
  position: fixed;
  bottom: 6.5rem;
  right: 2rem;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #0066ff 0%, #0052cc 100%);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 8px 25px rgba(0, 102, 255, 0.4);
  z-index: 999;
  transition: all 0.3s ease;
  overflow: visible;
}

.phone-button:hover {
  transform: scale(1.1);
  box-shadow: 0 12px 35px rgba(0, 102, 255, 0.6);
}

.phone-button:active {
  transform: scale(0.95);
}

.phone-icon {
  font-size: 28px;
  color: #ffffff;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.phone-tooltip {
  position: absolute;
  right: 75px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(10, 11, 20, 0.95);
  color: #ffffff;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  font-size: 0.875rem;
  white-space: nowrap;
  opacity: 0;
  transition: all 0.3s ease;
  pointer-events: none;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.phone-button:hover .phone-tooltip {
  opacity: 1;
  right: 80px;
}

/* Responsive */
@media (max-width: 1024px) {
  .nav-content .logo-img {
    width: 45px;
    height: 45px;
  }

  .logo-text {
    font-size: 1.8rem;
  }

  .hero-content {
    grid-template-columns: 1fr;
    gap: 3rem;
  }

  .hero-visual {
    height: 500px;
  }

  .flip-card {
    width: 350px;
    height: 224px;
  }

  .heading_8264 {
    font-size: 0.55em;
    top: 2.2em;
    left: 17.5em;
  }

  .flip-card .logo {
    top: 7.5em;
    left: 13em;
    width: 42px;
    height: 42px;
  }

  .chip {
    top: 2.5em;
    left: 1.8em;
    width: 36px;
    height: 36px;
  }

  .contactless {
    top: 4.2em;
    left: 14.5em;
    width: 24px;
    height: 24px;
  }

  .number {
    font-size: 0.75em;
    top: 9.5em;
    left: 1.8em;
  }

  .valid_thru {
    top: 15.5em;
    font-size: 0.45em;
    left: 3.5em;
  }

  .date_8264 {
    font-size: 0.6em;
    top: 17em;
    left: 3.5em;
  }

  .name {
    font-size: 0.6em;
    top: 19em;
    left: 2.2em;
  }

  .stats-container {
    grid-template-columns: repeat(2, 1fr);
  }

  .features-grid,
  .benefits-grid,
  .pricing-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .testimonials-grid {
    grid-template-columns: 1fr;
  }

  .integrations-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .section-title {
    font-size: 2.5rem;
  }

  .widget-profit {
    width: 200px;
    top: 3%;
    left: 2%;
  }

  .transaction-notifications {
    top: 3%;
    right: 2%;
  }

  .notification-card {
    width: 240px;
  }

  .footer-container {
    grid-template-columns: 1fr 1fr;
  }

  .footer-links {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .navbar {
    padding: 1rem;
  }

  .nav-content {
    padding: 0;
  }

  .nav-content .logo {
    gap: 0.5rem;
  }

  .nav-content .logo-img {
    width: 35px;
    height: 35px;
  }

  .logo-text {
    font-size: 1.4rem;
  }

  .nav-links {
    gap: 0.5rem;
  }

  .nav-link {
    display: none;
  }

  .btn-nav-login {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  }

  .btn-nav-login span:last-child {
    display: none;
  }

  /* Hero Section */
  .hero {
    padding: 1.5rem 1rem;
    min-height: auto;
    overflow-x: hidden;
  }

  .hero-content {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 2rem 1rem;
    padding-top: calc(2rem + 80px);
    min-height: auto;
  }

  .hero-text {
    text-align: center;
    width: 100%;
    max-width: 100%;
  }

  .hero-badge {
    font-size: 0.7rem;
    padding: 0.5rem 0.875rem;
    margin-bottom: 1rem;
    width: fit-content;
    margin-left: auto;
    margin-right: auto;
    white-space: normal;
    text-align: center;
  }

  .hero-badge span:last-child {
    font-size: 0.65rem;
    display: block;
    text-align: center;
    line-height: 1.3;
  }

  .hero-title {
    font-size: 1.75rem;
    line-height: 1.2;
    margin-bottom: 1rem;
    word-wrap: break-word;
  }

  .hero-subtitle {
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
    line-height: 1.5;
    padding: 0 0.5rem;
  }

  .hero-buttons {
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    max-width: 100%;
  }

  .btn-primary-hero,
  .btn-secondary-hero {
    width: 100%;
    padding: 0.875rem 1.25rem;
    font-size: 0.9rem;
    white-space: nowrap;
  }

  .hero-visual {
    height: 300px;
    min-height: 300px;
    position: relative;
    width: 100%;
    overflow: hidden;
  }

  .flip-card {
    width: 280px;
    height: 180px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    margin: 0;
    max-width: calc(100% - 2rem);
  }

  .heading_8264 {
    font-size: 0.45em;
    top: 1.8em;
    left: 12em;
  }

  .flip-card .logo {
    top: 6em;
    left: 9em;
    width: 30px;
    height: 30px;
  }

  .chip {
    top: 2em;
    left: 1.2em;
    width: 28px;
    height: 28px;
  }

  .contactless {
    top: 3.5em;
    left: 10.5em;
    width: 18px;
    height: 18px;
  }

  .number {
    font-size: 0.6em;
    top: 7.5em;
    left: 1.2em;
  }

  .valid_thru {
    top: 12em;
    font-size: 0.35em;
    left: 2.5em;
  }

  .date_8264 {
    font-size: 0.5em;
    top: 13.5em;
    left: 2.5em;
  }

  .name {
    font-size: 0.5em;
    top: 15.5em;
    left: 1.8em;
  }

  .strip {
    width: calc(100% - 1.5rem);
    height: 1.5em;
    top: 2em;
    left: 0.75rem;
  }

  .mstrip {
    width: 7em;
    height: 0.8em;
    top: 4.8em;
    left: 0.75rem;
  }

  .sstrip {
    width: 4em;
    height: 0.8em;
    top: 4.8em;
    left: 8.5em;
  }

  .code {
    font-size: 0.65em;
  }

  /* Floating Widgets */
  .widget-profit {
    width: 160px;
    padding: 0.75rem;
    top: auto;
    bottom: 20%;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.75rem;
    position: absolute;
    z-index: 5;
    max-width: calc(100% - 2rem);
  }

  .widget-header {
    margin-bottom: 0.5rem;
  }

  .widget-icon {
    font-size: 1rem;
  }

  .widget-title {
    font-size: 0.75rem;
  }

  .widget-value {
    font-size: 1.25rem;
  }

  .widget-subtitle {
    font-size: 0.7rem;
  }

  .transaction-notifications {
    top: auto;
    bottom: 2%;
    right: 50%;
    transform: translateX(50%);
    gap: 0.5rem;
    position: absolute;
    z-index: 5;
    max-width: calc(100% - 2rem);
  }

  .notification-card {
    width: 180px;
    padding: 0.625rem;
    font-size: 0.75rem;
  }

  .notification-avatar {
    width: 28px;
    height: 28px;
    font-size: 0.75rem;
  }

  .notification-amount {
    font-size: 0.875rem;
  }

  .notification-from {
    font-size: 0.7rem;
  }

  .notification-amount {
    font-size: 0.875rem;
  }

  .notification-from {
    font-size: 0.7rem;
  }

  .icon-nodes {
    display: none;
  }

  /* Stats Section */
  .stats-section {
    padding: 3rem 1rem;
  }

  .stats-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  .stat-card {
    padding: 1.5rem 1rem;
  }

  .stat-value {
    font-size: 2rem;
  }

  .stat-label {
    font-size: 0.875rem;
  }

  /* Section Styles */
  .section-container {
    padding: 2.5rem 1rem;
    max-width: 100%;
    overflow-x: hidden;
    box-sizing: border-box;
  }

  .section-header {
    padding: 0 0.5rem;
  }

  .section-header {
    margin-bottom: 2rem;
  }

  .section-badge {
    font-size: 0.75rem;
    padding: 0.5rem 1rem;
  }

  .section-title {
    font-size: 1.75rem;
    line-height: 1.3;
  }

  .section-subtitle {
    font-size: 1rem;
  }

  /* Features, Benefits, Testimonials */
  .features-grid,
  .benefits-grid,
  .testimonials-grid,
  .pricing-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .feature-card,
  .benefit-card,
  .testimonial-card,
  .pricing-card {
    padding: 1.5rem;
  }

  .feature-icon {
    font-size: 2.5rem;
  }

  .feature-title,
  .benefit-title,
  .pricing-name {
    font-size: 1.25rem;
  }

  .pricing-card.featured {
    transform: none;
  }

  .pricing-badge {
    font-size: 0.75rem;
    padding: 0.4rem 1rem;
  }

  .price-amount {
    font-size: 2.5rem;
  }

  /* Integrations */
  .integrations-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  .integration-card {
    padding: 1.5rem 1rem;
  }

  .integration-icon {
    width: 50px;
    height: 50px;
  }

  /* CTA Section */
  .cta-section {
    padding: 3rem 1rem;
  }

  .cta-title {
    font-size: 1.75rem;
    line-height: 1.3;
  }

  .cta-subtitle {
    font-size: 1rem;
    margin-bottom: 2rem;
  }

  .cta-buttons {
    flex-direction: column;
    gap: 1rem;
  }

  .btn-cta-primary,
  .btn-cta-secondary {
    width: 100%;
    padding: 1rem 1.5rem;
    font-size: 1rem;
  }

  /* Footer */
  .footer {
    padding: 3rem 1rem 1.5rem;
  }

  .footer-container {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .footer-links {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .footer-bottom {
    padding-top: 1.5rem;
    font-size: 0.8rem;
  }

  /* WhatsApp Button Mobile */
  .whatsapp-button {
    width: 56px;
    height: 56px;
    bottom: 1.5rem;
    right: 1.5rem;
  }

  .whatsapp-icon {
    width: 28px;
    height: 28px;
  }

  .whatsapp-tooltip {
    display: none;
  }

  .phone-button {
    width: 56px;
    height: 56px;
    bottom: 6rem;
    right: 1.5rem;
  }

  .phone-icon {
    font-size: 24px;
  }

  .phone-tooltip {
    display: none;
  }
}

/* Extra Small Devices */
@media (max-width: 480px) {
  .hero-title {
    font-size: 1.75rem;
  }

  .hero-subtitle {
    font-size: 0.9rem;
  }

  .flip-card {
    width: 260px;
    height: 167px;
  }

  .widget-profit {
    width: 160px;
    padding: 0.75rem;
    font-size: 0.75rem;
  }

  .widget-value {
    font-size: 1.25rem;
  }

  .transaction-notifications {
    display: none;
  }

  .stats-container {
    grid-template-columns: 1fr;
  }

  .section-title {
    font-size: 1.5rem;
  }

  .section-subtitle {
    font-size: 0.9rem;
  }

  .feature-card,
  .benefit-card,
  .testimonial-card,
  .pricing-card {
    padding: 1.25rem;
  }

  .integrations-grid {
    grid-template-columns: 1fr;
  }

  .cta-title {
    font-size: 1.5rem;
  }

  .btn-cta-primary,
  .btn-cta-secondary {
    padding: 0.875rem 1.25rem;
    font-size: 0.9rem;
  }
}

/* ========================================
   Lightbox / Image Modal Styles
======================================== */
.lightbox-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  backdrop-filter: blur(10px);
}

.lightbox-content {
  max-width: 90vw;
  max-height: 85vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lightbox-image {
  max-width: 100%;
  max-height: 85vh;
  object-fit: contain;
  border-radius: 16px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.7), 0 0 0 2px rgba(198, 146, 61, 0.4);
  animation: lightbox-zoom-in 0.3s ease-out;
}

@keyframes lightbox-zoom-in {
  from {
    transform: scale(0.8);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
}

.lightbox-close {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 2px solid rgba(198, 146, 61, 0.6);
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  color: #DFC05E;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;
  z-index: 10001;
}

.lightbox-close:hover {
  background: rgba(198, 146, 61, 0.3);
  border-color: rgba(198, 146, 61, 1);
  transform: scale(1.1);
}

.lightbox-close .material-symbols-rounded {
  font-size: 1.75rem;
  font-weight: 600;
}

.lightbox-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 2px solid rgba(198, 146, 61, 0.6);
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  color: #DFC05E;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;
  z-index: 10001;
}

.lightbox-nav:hover {
  background: rgba(198, 146, 61, 0.3);
  border-color: rgba(198, 146, 61, 1);
  transform: translateY(-50%) scale(1.1);
}

.lightbox-nav .material-symbols-rounded {
  font-size: 2rem;
  font-weight: 600;
}

.lightbox-prev {
  left: 1.5rem;
}

.lightbox-next {
  right: 1.5rem;
}

.lightbox-counter {
  position: absolute;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  padding: 0.75rem 1.5rem;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(198, 146, 61, 0.4);
  border-radius: 30px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.1em;
}

/* Lightbox Transition */
.lightbox-fade-enter-active,
.lightbox-fade-leave-active {
  transition: opacity 0.3s ease;
}

.lightbox-fade-enter-from,
.lightbox-fade-leave-to {
  opacity: 0;
}

/* Zoom icon overlay for carousel center slide */
.zoom-icon-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0);
  transition: all 0.3s ease;
  pointer-events: none;
  border-radius: 20px;
}

.carousel-curved-slide.is-center:hover .zoom-icon-overlay {
  background: rgba(0, 0, 0, 0.4);
}

.zoom-icon-overlay .material-symbols-rounded {
  font-size: 3rem;
  color: rgba(255, 255, 255, 0);
  transition: all 0.3s ease;
  transform: scale(0.8);
}

.carousel-curved-slide.is-center:hover .zoom-icon-overlay .material-symbols-rounded {
  color: rgba(255, 255, 255, 0.95);
  transform: scale(1);
}

/* Make center slide cursor pointer for lightbox */
.carousel-curved-slide.is-center {
  cursor: pointer;
}

/* Mobile Lightbox Adjustments */
@media (max-width: 768px) {
  .lightbox-overlay {
    padding: 1rem;
  }

  .lightbox-close {
    top: 1rem;
    right: 1rem;
    width: 44px;
    height: 44px;
  }

  .lightbox-close .material-symbols-rounded {
    font-size: 1.5rem;
  }

  .lightbox-nav {
    width: 48px;
    height: 48px;
  }

  .lightbox-nav .material-symbols-rounded {
    font-size: 1.75rem;
  }

  .lightbox-prev {
    left: 0.75rem;
  }

  .lightbox-next {
    right: 0.75rem;
  }

  .lightbox-counter {
    bottom: 1rem;
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
  }

  .lightbox-image {
    border-radius: 12px;
  }

  .zoom-icon-overlay .material-symbols-rounded {
    font-size: 2.5rem;
  }
}

/* Hero Image Update */
.hero-image-wrapper {
  position: relative;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
}

.hero-main-img {
  width: 100%;
  height: auto;
  border-radius: 50%;
  box-shadow: 0 0 50px rgba(198, 146, 61, 0.2);
  transform: rotate(15deg);
  transition: transform 0.5s ease;
}

.hero-main-img:hover {
  transform: rotate(0deg);
}

.floating-badge {
  position: absolute;
  background: rgba(20, 20, 25, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(198, 146, 61, 0.4);
  border-radius: 12px;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.5);
  animation: float 4s ease-in-out infinite;
}

.badge-quality {
  top: 10%;
  left: -10%;
}

.badge-taste {
  bottom: 10%;
  right: -5%;
  animation-delay: 2s;
}

.floating-badge .material-symbols-rounded {
  color: #DFC05E;
  font-size: 1.5rem;
}

.badge-text {
  display: flex;
  flex-direction: column;
}

.badge-title {
  color: #fff;
  font-weight: 600;
  font-size: 0.9rem;
}

.badge-desc {
  color: #a0a0a0;
  font-size: 0.8rem;
}

@keyframes float {
  0% {
    transform: translateY(0px);
  }

  50% {
    transform: translateY(-10px);
  }

  100% {
    transform: translateY(0px);
  }
}

/* Featured Dish */
.featured-dish-section {
  padding: 6rem 2rem;
}

.featured-dish-content {
  display: flex;
  gap: 4rem;
  align-items: center;
}

.featured-dish-image-wrapper {
  flex: 1;
}

.featured-dish-image {
  width: 100%;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
}

.featured-dish-info {
  flex: 1;
}

.featured-dish-name {
  font-size: 2.5rem;
  color: #fff;
  font-family: 'Playfair Display', serif;
  margin-bottom: 1rem;
}

.featured-dish-desc {
  color: #a0a0a0;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
}

.featured-dish-price {
  font-size: 2rem;
  color: #DFC05E;
  font-weight: bold;
  margin-bottom: 2rem;
}

/* Menu Grid */
.menu-section {
  padding: 6rem 2rem;
}

.menu-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
}

.menu-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 1.5rem;
  transition: transform 0.3s ease;
}

.menu-card:hover {
  transform: translateY(-5px);
  border-color: rgba(223, 192, 94, 0.3);
}

.menu-image-container {
  width: 100%;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1rem;
}

.menu-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.menu-name {
  color: #fff;
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
  font-family: 'Playfair Display', serif;
}

.menu-description {
  color: #a0a0a0;
  font-size: 0.9rem;
  line-height: 1.4;
  margin-bottom: 1rem;
  min-height: 60px;
}

.menu-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.menu-price {
  color: #DFC05E;
  font-weight: bold;
  font-size: 1.2rem;
}

.btn-menu-add {
  background: #C6923D;
  color: #fff;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-menu-add:hover {
  background: #DFC05E;
}

.menu-show-more {
  text-align: center;
  display: flex;
  justify-content: center;
}

/* Services */
.services-section {
  padding: 6rem 2rem;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
}

.service-card {
  text-align: center;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.service-icon-wrapper {
  margin: 0 auto 1.5rem;
  width: 64px;
  height: 64px;
  background: rgba(198, 146, 61, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #DFC05E;
}

.service-icon-wrapper .material-symbols-rounded {
  font-size: 2rem;
}

.service-title {
  color: #fff;
  font-size: 1.2rem;
  margin-bottom: 1rem;
}

.service-description {
  color: #a0a0a0;
  font-size: 0.95rem;
  line-height: 1.5;
}

/* Video Section */
.video-promo-section {
  padding: 0;
  height: 60vh;
  position: relative;
  overflow: hidden;
  margin: 6rem 0;
}

.video-promo-content {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-promo-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.4);
  z-index: 1;
}

.video-text-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.3) 50%, transparent 100%);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 4rem;
}

@media (max-width: 768px) {
  .featured-dish-content {
    flex-direction: column;
  }
}

/* Custom Hero Background Overlay */
.hero {
  position: relative;
  background-image: url('/img/restaurant_bg.png');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
}

.hero::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(10, 11, 20, 0.75);
  z-index: 0;
}

.hero-content,
.hero-background,
.navbar {
  position: relative;
  z-index: 10;
}

/* Global Mesh Pattern */
.landing-page::before {
  content: "";
  position: fixed;
  inset: 0;
  background-image: radial-gradient(circle at 2px 2px, rgba(255, 255, 255, 0.03) 1px, transparent 0);
  background-size: 32px 32px;
  pointer-events: none;
  z-index: 0;
}

/* Typography Enhancements */
.hero-title {
  font-size: clamp(3rem, 8vw, 5rem) !important;
  text-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  background: linear-gradient(to bottom, #fff 40%, #DFC05E 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.1;
  margin-bottom: 1.5rem;
}

.hero-subtitle {
  font-size: 1.25rem !important;
  max-width: 600px;
  margin-bottom: 2.5rem;
  color: #a0a0a0 !important;
  line-height: 1.6;
}

/* Button Refinement */
.btn-nav-login {
  background: linear-gradient(135deg, #DFC05E 0%, #C6923D 100%);
  color: #000;
  border: none;
  padding: 0.6rem 1.4rem;
  border-radius: 50px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 4px 15px rgba(198, 146, 61, 0.3);
}

.btn-nav-login:hover {
  transform: scale(1.05) translateY(-2px);
  box-shadow: 0 8px 25px rgba(198, 146, 61, 0.4);
}

.btn-nav-login .material-symbols-rounded {
  font-size: 1.2rem;
}

/* Modern Navbar Scrolled State */
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 1.5rem 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.navbar-scrolled {
  background: rgba(10, 11, 20, 0.9) !important;
  backdrop-filter: blur(20px) saturate(180%) !important;
  padding: 0.5rem 0 !important;
  border-bottom: 1px solid rgba(198, 146, 61, 0.3);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

/* Scroll Reveal Animations */
.scroll-reveal {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

/* Menu Card Hover Glow */
.menu-card {
  position: relative;
  transition: all 0.4s ease;
}

.menu-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(198, 146, 61, 0.1);
  border-color: rgba(198, 146, 61, 0.3) !important;
}

/* Parallax Adjustments */
.hero-visual {
  transition: transform 0.1s ease-out;
}

.gradient-orb {
  transition: transform 0.1s ease-out;
}

/* Hero Main Image Floating Animation */
@keyframes float-dish {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-15px);
  }
}

.hero-main-img {
  animation: float-dish 6s infinite ease-in-out;
}

.hero-image-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120%;
  height: 120%;
  background: radial-gradient(circle, rgba(198, 146, 61, 0.15) 0%, transparent 70%);
  z-index: -1;
  pointer-events: none;
}

/* Menu Description Clamping */
.menu-description {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 4.5em;
  /* Approximately 3 lines */
}

/* Custom Modal Styles (Inspired by ConfirmModal.vue) */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1.5rem;
}

.modal-container {
  background: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 28px;
  width: 100%;
  max-width: 440px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  animation: modal-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes modal-pop {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }

  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-content {
  padding: 2.5rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
}

.icon-wrapper.warning {
  background: linear-gradient(135deg, #DFC05E 0%, #C6923D 100%);
  box-shadow: 0 8px 16px rgba(223, 192, 94, 0.2);
}

.icon-wrapper .icon {
  font-size: 3rem;
  color: #000;
}

.modal-title {
  font-family: 'Playfair Display', serif;
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
}

.modal-title.warning {
  color: #DFC05E;
}

.modal-message {
  color: #a0a0a0;
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 0;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  width: 100%;
  margin-top: 1rem;
}

.btn-modal-cancel,
.btn-modal-confirm {
  flex: 1;
  padding: 1rem;
  border: none;
  border-radius: 14px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-modal-cancel {
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
}

.btn-modal-cancel:hover {
  background: rgba(255, 255, 255, 0.1);
}

.btn-modal-confirm.warning {
  background: linear-gradient(135deg, #DFC05E 0%, #C6923D 100%);
  color: #000;
}

.btn-modal-confirm:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(198, 146, 61, 0.3);
}

/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.video-promo-text {
  color: #fff;
  max-width: 700px;
  animation: slideUp 1s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.video-promo-title {
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-family: 'Playfair Display', serif;
  margin-bottom: 1.5rem;
  color: #DFC05E;
  font-weight: 700;
  line-height: 1.1;
}

.video-promo-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.video-promo-item {
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1.2rem;
  color: #e0e0e0;
}

.check-icon {
  color: #DFC05E;
  font-size: 1.8rem;
}

@media (max-width: 768px) {
  .video-promo-section {
    height: auto;
    min-height: 400px;
  }

  .video-text-overlay {
    padding: 2rem;
    align-items: flex-end;
  }

  .video-promo-title {
    font-size: 2rem;
  }

  .video-promo-item {
    font-size: 1rem;
  }
}
</style>